import { issuerAvailableMinor } from './select'
import type {
  AuthOutcome,
  Channel,
  DeclineCategory,
  DecisionMaker,
  EvaluationStep,
  Minor,
  PresentedCredential,
  ResponseCode,
  World,
} from './types'

export interface AuthRequest {
  cardId: string
  merchantId: string
  channel: Channel
  amountMinor: Minor
  credential: PresentedCredential
}

/**
 * Visa's decline categories govern whether a merchant is allowed to retry.
 * Category 1 means the issuer will never approve, so a reattempt is pointless
 * and attracts Visa penalties.
 *
 * ILLUSTRATIVE. Verify against the current published Visa table before relying
 * on any specific assignment here. See docs/sources.md.
 */
export const CATEGORY: Partial<Record<ResponseCode, DeclineCategory>> = {
  '05': 4,
  '14': 1,
  '46': 1,
  '51': 2,
  '54': 1,
  '57': 1,
  '59': 1,
  '61': 2,
  '62': 1,
  '65': 2,
  '91': 3,
}

export const CATEGORY_MEANING: Record<DeclineCategory, string> = {
  1: 'Issuer will never approve. Do not retry.',
  2: 'Issuer cannot approve right now. A later retry may succeed.',
  3: 'Something was wrong with the message or the route. Fix and retry.',
  4: 'Generic. The reason is not disclosed, which is why Visa presses issuers to use these less.',
}

/** A check made at VisaNet, before the issuer host is ever consulted. */
export interface SchemeCheck {
  stage: 'routing' | 'vrm' | 'stip'
  question: string
  passed: boolean
  detail: string
  responseCode?: ResponseCode
  /** The same check with the jargon taken out, for the plain depth. */
  plain: { question: string; detail: string }
}

export interface CheckResult {
  step: EvaluationStep
  question: string
  passed: boolean
  responseCode?: ResponseCode
  detail: string
}

export interface AuthDecision {
  outcome: AuthOutcome
  responseCode: ResponseCode
  category?: DeclineCategory
  decidedBy: DecisionMaker
  decidedAt: EvaluationStep
  amountApprovedMinor: Minor
  /** Everything VisaNet did before, or instead of, asking the issuer. */
  schemeChecks: SchemeCheck[]
  /** Empty when the issuer never saw the transaction. */
  issuerChecks: CheckResult[]
  standIn: boolean
  /** False when the issuer does not yet know this transaction happened. */
  advised: boolean
  /** True when the issuer host was never reached. */
  issuerBypassed: boolean
}

const pass = (step: EvaluationStep, question: string, detail: string): CheckResult => ({
  step,
  question,
  passed: true,
  detail,
})

const fail = (
  step: EvaluationStep,
  question: string,
  responseCode: ResponseCode,
  detail: string,
): CheckResult => ({ step, question, passed: false, responseCode, detail })

/**
 * Velocity counters can only count what the issuer knows about. A stand-in
 * approval it has not been advised of is invisible here too, which is why
 * cumulative limits can be quietly breached during an outage.
 */
function spentTodayMinor(world: World, cardId: string): Minor {
  return world.transactions
    .filter(
      (t) => t.cardId === cardId && t.day === world.day && t.outcome !== 'declined' && t.advised,
    )
    .reduce((sum, t) => sum + t.amountApprovedMinor, 0)
}

/**
 * The full journey. VisaNet gets first look, and it can finish the
 * transaction without the issuer ever hearing about it, in two quite different
 * ways: a risk rule the issuer configured, or standing in because the issuer
 * host did not answer.
 */
export function evaluateAuth(world: World, req: AuthRequest): AuthDecision {
  const schemeChecks: SchemeCheck[] = []
  const issuerChecks: CheckResult[] = []
  const card = world.cards.find((c) => c.id === req.cardId)
  const merchant = world.merchants.find((m) => m.id === req.merchantId)
  const cfg = world.config

  const declined = (
    responseCode: ResponseCode,
    decidedBy: DecisionMaker,
    decidedAt: EvaluationStep,
    opts: { standIn?: boolean; issuerBypassed?: boolean } = {},
  ): AuthDecision => ({
    outcome: 'declined',
    responseCode,
    ...(CATEGORY[responseCode] !== undefined ? { category: CATEGORY[responseCode]! } : {}),
    decidedBy,
    decidedAt,
    amountApprovedMinor: 0,
    schemeChecks,
    issuerChecks,
    standIn: opts.standIn ?? false,
    advised: true,
    issuerBypassed: opts.issuerBypassed ?? false,
  })

  // ---- VisaNet: routing -----------------------------------------------------
  if (!card || !merchant) {
    schemeChecks.push({
      stage: 'routing',
      question: 'Can VisaNet route this?',
      passed: false,
      responseCode: '14',
      detail: 'The account number does not resolve to a Visa issuer.',
      plain: {
        question: 'Can Visa tell which bank to ask?',
        detail: 'That card number does not belong to any bank Visa knows.',
      },
    })
    return declined('14', 'scheme-routing', 'technical', { issuerBypassed: true })
  }
  schemeChecks.push({
    stage: 'routing',
    question: 'Can VisaNet route this?',
    passed: true,
    detail: `Account number range resolves to the issuer. Acquirer presented it from ${merchant.country}.`,
    plain: {
      question: 'Can Visa tell which bank to ask?',
      detail: 'Yes. The first digits of the card number say which bank issued it.',
    },
  })

  // ---- VisaNet: Visa Risk Manager ------------------------------------------
  // These are the issuer's own rules. Visa is enforcing them, not
  // inventing them, and a decline here never reaches the issuer host.
  for (const rule of cfg.vrmRules) {
    if (!rule.enabled) continue
    const channelMatches = rule.channel === undefined || rule.channel === req.channel
    const overThreshold =
      rule.thresholdMinor === undefined || req.amountMinor > rule.thresholdMinor
    if (channelMatches && overThreshold) {
      schemeChecks.push({
        stage: 'vrm',
        question: 'Do the issuer’s Visa Risk Manager rules permit this?',
        passed: false,
        responseCode: rule.responseCode,
        detail: `${rule.label}. Visa Risk Manager refused it at VisaNet, so the issuer host never saw this transaction.`,
        plain: {
          question: 'Do your bank’s rules at Visa allow this?',
          detail: `No. ${rule.label}. Visa turned it down for your bank, and your bank never saw it.`,
        },
      })
      return declined(rule.responseCode, 'scheme-vrm', 'fraud', { issuerBypassed: true })
    }
  }
  if (cfg.vrmRules.some((r) => r.enabled)) {
    schemeChecks.push({
      stage: 'vrm',
      question: 'Do the issuer’s Visa Risk Manager rules permit this?',
      passed: true,
      detail: 'No enabled Visa Risk Manager rule matched, so the transaction carries on to the issuer.',
      plain: {
        question: 'Do your bank’s rules at Visa allow this?',
        detail: 'Yes. None of the rules your bank left with Visa applied, so it carries on to your bank.',
      },
    })
  }

  // ---- VisaNet: is the issuer actually there? ------------------------------
  if (!cfg.issuerReachable) {
    if (!cfg.stip.enabled) {
      schemeChecks.push({
        stage: 'stip',
        question: 'Is the issuer host answering?',
        passed: false,
        responseCode: '91',
        detail: 'Issuer host did not respond, and Stand-In Processing is switched off for this issuer.',
        plain: {
          question: 'Is your bank answering?',
          detail: 'No, and your bank has not asked Visa to answer for it, so nobody can say yes.',
        },
      })
      return declined('91', 'scheme-routing', 'technical', { issuerBypassed: true })
    }

    // Stand-In. Visa decides on the issuer's behalf, inside limits the
    // issuer agreed in advance. It cannot see the balance, so it does not check it.
    const withinLimit = req.amountMinor <= cfg.stip.limitMinor
    schemeChecks.push({
      stage: 'stip',
      question: 'Is the issuer host answering?',
      passed: false,
      detail: 'Issuer host did not respond inside the V.I.P. System time budget. Visa Stand-In Processing takes over.',
      plain: {
        question: 'Is your bank answering?',
        detail: 'No. Visa steps in and answers for it, using rules your bank agreed in advance.',
      },
    })
    schemeChecks.push({
      stage: 'stip',
      question: 'Is it inside the Stand-In parameters the issuer agreed?',
      passed: withinLimit,
      ...(withinLimit ? {} : { responseCode: '61' as ResponseCode }),
      detail: withinLimit
        ? 'Inside the Stand-In ceiling. Visa approves without any sight of the balance, and a 0120 advice follows to the issuer.'
        : 'Above the Stand-In ceiling the issuer agreed, so Visa refuses rather than guess.',
      plain: {
        question: 'Is it small enough for Visa to approve on its own?',
        detail: withinLimit
          ? 'Yes. Visa says yes without being able to see your balance, and tells your bank afterwards.'
          : 'No. It is more than your bank allows Visa to approve blind, so Visa says no rather than risk it.',
      },
    })

    if (!withinLimit) {
      return declined('61', 'scheme-stip', 'controls', { standIn: true, issuerBypassed: true })
    }

    return {
      outcome: 'approved',
      responseCode: '00',
      decidedBy: 'scheme-stip',
      decidedAt: 'funds',
      amountApprovedMinor: req.amountMinor,
      schemeChecks,
      issuerChecks,
      standIn: true,
      // The issuer does not know yet. Its balance is wrong until the advice lands.
      advised: false,
      issuerBypassed: true,
    }
  }

  schemeChecks.push({
    stage: 'routing',
    question: 'Is the issuer host answering?',
    passed: true,
    detail: 'Issuer host responded inside the V.I.P. System time budget, so it makes the decision itself.',
    plain: {
      question: 'Is your bank answering?',
      detail: 'Yes, and quickly enough, so your bank decides this itself.',
    },
  })

  // ---- Issuer -------------------------------------------------------------
  const decideIssuer = (c: CheckResult): AuthDecision => {
    issuerChecks.push(c)
    return declined(c.responseCode ?? '05', 'issuer', c.step)
  }

  issuerChecks.push(
    pass(
      'technical',
      'Is the credential recognised?',
      req.credential.kind === 'token'
        ? req.credential.cryptogramPresent
          ? 'Token detokenised by the Visa Token Service and the cryptogram verified.'
          : 'Token detokenised by the Visa Token Service. No cryptogram, which is permitted for a stored ecommerce token.'
        : 'Account number recognised and the 0100 message is well formed.',
    ),
  )

  if (world.account.status !== 'open') {
    return decideIssuer(
      fail('card-state', 'Is the card usable?', '62', `Account is ${world.account.status}.`),
    )
  }
  switch (card.state) {
    case 'active':
      issuerChecks.push(pass('card-state', 'Is the card usable?', 'Card is active.'))
      break
    case 'expired':
      return decideIssuer(
        fail('card-state', 'Is the card usable?', '54', 'Card is past its expiry date.'),
      )
    case 'unactivated':
      return decideIssuer(
        fail('card-state', 'Is the card usable?', '62', 'Card was issued but never activated.'),
      )
    case 'suspended':
      return decideIssuer(fail('card-state', 'Is the card usable?', '62', 'Card is suspended.'))
    case 'closed':
      return decideIssuer(
        fail('card-state', 'Is the card usable?', '46', 'Card is closed, and that is terminal.'),
      )
    case 'created':
      return decideIssuer(
        fail('card-state', 'Is the card usable?', '14', 'No usable credential exists yet.'),
      )
  }

  const ctl = card.controls
  const q = 'Do the card controls permit this?'
  if (!ctl.channels[req.channel]) {
    return decideIssuer(fail('controls', q, '57', `The ${req.channel} channel is switched off.`))
  }
  if (ctl.blockedMccs.includes(merchant.mcc)) {
    return decideIssuer(fail('controls', q, '57', `Merchant category ${merchant.mcc} is blocked.`))
  }
  if (ctl.allowedMccs.length > 0 && !ctl.allowedMccs.includes(merchant.mcc)) {
    return decideIssuer(
      fail('controls', q, '57', `Merchant category ${merchant.mcc} is not on the allow list.`),
    )
  }
  if (ctl.allowedCountries.length > 0 && !ctl.allowedCountries.includes(merchant.country)) {
    return decideIssuer(
      fail('controls', q, '57', `Transactions in ${merchant.country} are not permitted.`),
    )
  }
  if (ctl.perTransactionLimitMinor !== undefined && req.amountMinor > ctl.perTransactionLimitMinor) {
    return decideIssuer(fail('controls', q, '61', 'Amount is over the per-transaction limit.'))
  }
  if (
    ctl.dailyLimitMinor !== undefined &&
    spentTodayMinor(world, card.id) + req.amountMinor > ctl.dailyLimitMinor
  ) {
    return decideIssuer(fail('controls', q, '65', 'Amount is over the daily limit.'))
  }
  issuerChecks.push(pass('controls', q, 'No control refused this transaction.'))

  issuerChecks.push(
    pass('fraud', 'Does this look like the cardholder?', 'Visa Advanced Authorization returned a score with the request, below the issuer’s refusal threshold.'),
  )

  // The issuer checks the balance it believes it has. Any stand-in approval it
  // has not been advised of yet is invisible here, which is how issuers end up
  // approving money that is already spoken for.
  if (req.amountMinor > issuerAvailableMinor(world)) {
    return decideIssuer(
      fail('funds', 'Is the money there?', '51', 'Available balance will not cover the amount.'),
    )
  }
  issuerChecks.push(pass('funds', 'Is the money there?', 'Available balance covers the amount.'))

  return {
    outcome: 'approved',
    responseCode: '00',
    decidedBy: 'issuer',
    decidedAt: 'funds',
    amountApprovedMinor: req.amountMinor,
    schemeChecks,
    issuerChecks,
    standIn: false,
    advised: true,
    issuerBypassed: false,
  }
}
